import React, { useEffect, useState } from 'react';
import { publicApi } from '../api/publicClient';
import { injectTracking } from '../utils/tracking';
import { sanitizePageHtml } from '../utils/htmlSanitizer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

/**
 * Bypasses React Router to serve raw HTML for public visitors.
 * Sanitizes legacy S3 scripts and eliminates the API waterfall.
 */
export const PublicPageViewer = ({ slug }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const loadPageAndSite = async () => {
      try {
        // 1. Fetch Page Data from NoCodeBackend via publicApi
        const pageData = await publicApi.getPageBySlug(slug);
        if (!active) return;

        if (!pageData) {
          setError('Page not found or is not published.');
          setLoading(false);
          return;
        }

        // 2. Fetch Site Data if site_id exists
        let siteData = null;
        if (pageData.site_id) {
          try {
            siteData = await publicApi.getSiteById(pageData.site_id);
          } catch (e) {
            console.error('Failed to fetch site tracking data', e);
          }
        }

        if (!active) return;

        // 3. Runtime Cleanup: Strip Greta, Quest S3, Google Fonts, and ipapi.co
        let cleanHtml = sanitizePageHtml(pageData.html_content || '');
        cleanHtml = cleanHtml
          // Strips inline script blocks calling ipapi.co
          .replace(/<script[^>]*>[\s\S]*?ipapi\.co[\s\S]*?<\/script>/gi, '')
          // Strips Google Fonts API calls
          .replace(/<link[^>]*fonts\.googleapis\.com[^>]*>/gi, '')
          .replace(/<link[^>]*fonts\.gstatic\.com[^>]*>/gi, '');

        // 4. Inject tracking and replace DOM content
        const trackedHtml = injectTracking(cleanHtml, pageData, siteData);

        document.open();
        document.write(trackedHtml);
        document.close();
      } catch (err) {
        if (!active) return;
        console.error('[PublicPageViewer] Load error:', err);
        setError('An error occurred while loading this page.');
        setLoading(false);
      }
    };

    loadPageAndSite();

    return () => { active = false; };
  }, [slug]);

  if (loading) return (
    <div className="flex h-screen w-full items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
    </div>
  );

  if (error) return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-slate-50 px-4 text-center font-sans">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-200 mb-6">
        <SafeIcon icon={FiIcons.FiFileText} className="text-4xl text-slate-400" />
      </div>
      <h1 className="text-5xl font-extrabold text-slate-900 mb-4">404</h1>
      <p className="text-lg text-slate-600 max-w-sm">{error}</p>
    </div>
  );

  return null;
};